import clsx from 'clsx';
import React, { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router';

import SectionLoader, {
  LOADER_TYPE,
} from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { cn } from '@/src/util/ui';

import FileViewer from '../../../../components/EntryPanel/FileEntryViewer/components/FileViewer/FileViewer';
import InfoPanel from '../../../../components/EntryPanel/FileEntryViewer/components/InfoPanel/InfoPanel';
import { useConnectedApps } from '../../../../components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/api/getConnectedApps';
import { TreatmentsContextProvider } from '../../../../components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/store/treatments.store';
import SidebarActions from '../../../../components/EntryPanel/FileEntryViewer/components/SidebarActions/SidebarActions';
import { useFileEntryViewerSidebarActions } from '../../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.sidebarActions.store';
import { useFileEntryViewer } from '../../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.store';
import EntryCard from '../../../../Elements/EntryCard/EntryCard';
import { useInbox } from '../../../../store/inbox.store';
import { FileEntry, InboxEntryStatus } from '../../../../types';

const defaultSidebarSettings = {
  isDeleteEnabled: true,
  canMaximizeMinimize: false,
};

const fileViewerFulScreenClasses = {
  mainContainerClass: ['tw-absolute tw-inset-0 tw-z-[3]', 'tw-w-full', 'tw-bg-white'],
  entryCardClass: ['tw-h-full'],
};

// default height classes for file viewer
const getInfoPanelCustomClasses = (containerCustomClasses) => {
  const defaultClasses = {
    commentListHeight: 'tw-h-[calc(100vh-130px)]',
    activityListHeight: 'tw-h-[calc(100vh-90px)]',
    transactionListHeight: 'tw-h-[calc(100vh-70px)]',
    transactionFormHeight: 'tw-h-[calc(100vh-175px)]',
  };

  return { ...defaultClasses, ...containerCustomClasses };
};

type FileEntryViewerSettings = {
  isTransactionsEnabled: boolean;
};

type FileEntryViewerProps = {
  fileEntry: FileEntry;
  header: ReactElement;
  mainContainerClass?: string;
  entryCardClass?: string;
  settings?: FileEntryViewerSettings;
  isViewerFullScreen?: boolean;
  isContainerFullScreen?: boolean;
};

const FileEntryViewer = ({
  fileEntry,
  header,
  mainContainerClass,
  entryCardClass,
  isViewerFullScreen = true,
  isContainerFullScreen = true,
}: FileEntryViewerProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { sidebarActions } = useFileEntryViewerSidebarActions();
  const { updateStatusMutationListener, markReadMutationListener } = sidebarActions;
  const connectedAppsQuery = useConnectedApps({
    projectId,
  });
  const { isTransactionsEnabled, setIsTransactionsEnabled } = useInbox();

  useEffect(() => {
    setIsTransactionsEnabled(!!connectedAppsQuery.data?.quickbooks);
  }, [connectedAppsQuery.data, setIsTransactionsEnabled]);

  const containerCustomClasses = {
    commentListHeight: 'tw-h-[calc(100vh-110px)]',
    activityListHeight: 'tw-h-[calc(100vh-90px)]',
  };

  const customInfoPanelClasses = getInfoPanelCustomClasses(containerCustomClasses);

  const { isFullScreen, setFileEntry, activeTab, setActiveTab } = useFileEntryViewer();

  useEffect(() => {
    // Automatically open new files
    if (fileEntry.status === InboxEntryStatus.New) {
      updateStatusMutationListener.mutateAsync({
        status: InboxEntryStatus.Open,
      });
    }

    if (!fileEntry.isRead) {
      markReadMutationListener.mutateAsync({
        read: true,
      });
    }

    setFileEntry(fileEntry);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileEntry.id, fileEntry.status, setFileEntry]);

  if (isTransactionsEnabled === null) {
    return <SectionLoader loaderType={LOADER_TYPE.screen} />;
  }

  return (
    <ModalPortal isEnabled={isFullScreen}>
      <div
        className={clsx(
          'urlPage--container',
          'card--selectedEntryPanel',
          'tw-grow tw-flex',
          'tw-p-[10px]',
          'tw-overflow-auto tw-scroll-styles',
          //prop class to set designated styles for main container ( e.g. width )
          [...fileViewerFulScreenClasses.mainContainerClass],
          mainContainerClass
        )}
      >
        <EntryCard
          className={cn(
            'tw-flex tw-self-start tw-grow',
            'tw-rounded-[5px]',
            'hover:tw-bg-white',
            [...fileViewerFulScreenClasses.entryCardClass],
            //prop class to set designated styles for file card ( e.g. height )
            entryCardClass
          )}
          isActive={true}
        >
          <div
            className={clsx('container--selectedEntryViewer', 'tw-relative', 'tw-w-[50%] tw-grow')}
          >
            {/* header prop for different headers */}
            {header}

            <FileViewer
              file={{
                id: fileEntry.file.id,
                name: fileEntry.file.name,
                rawName: fileEntry.file.rawName,
                googleDriveFileUrl: fileEntry.file.googleDriveFileUrl,
                isFileSupported: fileEntry.file.isFileSupported,
              }}
              key={fileEntry.file.rawName}
            />
          </div>
          <React.Fragment>
            <TreatmentsContextProvider fileId={fileEntry.file.id} isViewInQuickbooksEnabled={false}>
              <div
                className={clsx(
                  'container--selectedEntryDetails',
                  'tw-border-l-[1px] tw-border-l-solid tw-border-l-cfo-grey-500',
                  'tw-w-[50%] tw-grow'
                )}
              >
                <InfoPanel
                  projectId={projectId}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  // transactionsEnabled={isTransactionsEnabled}
                  fileEntry={fileEntry}
                  //props boolean values to set the height classes of infopanel tabs if full screen
                  isViewerFullScreen={isViewerFullScreen}
                  isContainerFullScreen={isContainerFullScreen}
                  {...customInfoPanelClasses}
                />
              </div>
              <SidebarActions fileEntry={fileEntry} sidebarSettings={defaultSidebarSettings} />
            </TreatmentsContextProvider>
          </React.Fragment>
        </EntryCard>
      </div>
    </ModalPortal>
  );
};

export default FileEntryViewer;
